/** Default max dimension for image compression (pixels) */
export const IMAGE_MAX_DIMENSION = 1024;

/** Default JPEG quality for image compression (0.0 - 1.0) */
export const IMAGE_QUALITY = 0.7;

/**
 * Compress an image file to a maximum dimension and specific JPEG quality.
 * @param file The original image file
 * @param maxDimension The maximum width or height (default: IMAGE_MAX_DIMENSION)
 * @param quality The JPEG quality (0.0 - 1.0, default: IMAGE_QUALITY)
 * @returns A promise that resolves to the compressed File
 */
export async function compressImage(
  file: File,
  maxDimension = IMAGE_MAX_DIMENSION,
  quality = IMAGE_QUALITY
): Promise<File> {
  // If not an image, return original
  if (!file.type.startsWith('image/')) {
    return file
  }

  return new Promise((resolve, reject) => {
    const img = new Image()
    const reader = new FileReader()

    reader.onload = (e) => {
      img.src = e.target?.result as string
    }

    reader.onerror = (e) => reject(e)

    img.onload = () => {
      let width = img.width
      let height = img.height

      // Scale dimensions if necessary
      if (width > maxDimension || height > maxDimension) {
        if (width > height) {
          height = Math.round(height * (maxDimension / width))
          width = maxDimension
        } else {
          width = Math.round(width * (maxDimension / height))
          height = maxDimension
        }
      }

      // Draw to canvas
      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        reject(new Error('Failed to get canvas context'))
        return
      }
      ctx.drawImage(img, 0, 0, width, height)

      // Convert to blob
      canvas.toBlob(
        (blob) => {
          if (blob) {
            console.log(
              `Original: ${(file.size / 1024 / 1024).toFixed(2)}MB, Compressed: ${(
                blob.size /
                1024 /
                1024
              ).toFixed(2)}MB`
            )
            const compressedFile = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now(),
            })
            resolve(compressedFile)
          } else {
            reject(new Error('Compression failed'))
          }
        },
        'image/jpeg',
        quality
      )
    }

    img.onerror = (e) => reject(e)

    // Start reading
    reader.readAsDataURL(file)
  })
}
